import type { EnergyType } from 'types'
import type { Offer } from './seller'

export type MatchingCriteria = {
  surplus_percentage_limit: number
  maximal_number_of_production_energy_profiles_in_diff: number
  pricingFile?: File
}

export type MatchingOfferId = {
  match_id: number
}

export type BestMatchData = {
  match_result?: MatchingOfferId
  individual_energy_pricing_file_errors: string[]
}

export type MatchData = {
  match_result?: MatchingOfferId
  individual_energy_pricing_file_errors: string[]
}

export type Zone = {
  coverage_percentage: number
  shortage_percentage: number
  surplus_percentage: number
  total_coverage: number
  total_required: number
  total_shortage: number
  total_surplus: number
}

export type StatusHistory = {
  status: Offer['status']
  reason: string
  created_date: string
  modified_by: string
}

export type PricingType = 'GENERAL' | 'INDIVIDUAL'

export type CostDetails = {
  pricing_type: PricingType
  current_energy_price: number
  current_total_required_energy_price: number
  total_green_energy_price: number
  green_energy_price: number
  total_tranche_energy_price: number
  tranche_energy_price: number
  total_shortage_price: number
  shortage_price: number
  total_surplus_sell_price: number
  surplus_sell_price: number
  additional_coverage_costs: number
}

export type MatchStatus = 'ACTIVE' | 'RESERVED' | 'BLOCKED' | 'ACCEPTED' | 'FINALIZED' | 'DELETED'

export type MonthlyData = Zone & { zones_data: { [key: string]: Zone } }

export type YearlyData = {
  coverage_percentage: number
  shortage_percentage: number
  surplus_percentage: number
  total_coverage: number
  total_shortage: number
  total_surplus: number
  zones_data: {
    [key: string]: Zone
  }
  cost_details: {
    GENERAL: CostDetails
    INDIVIDUAL: CostDetails
  }
}

export type ClientYearlyData = YearlyData & {
  current_energy_price: number
  current_total_required_energy_price: number
}

export type MatchedOffersData = {
  offer_id: number
  energy_type: EnergyType
  energy_price: number
  total_power: number
}

export type TrancheKeyType = 'base' | 'peak'
export type TrancheKeyPeriod = 'y' | 'q' | 'm'
export type TrancheKeyValueType = 'power' | 'price' | 'lock'

export type TrancheKey = `${TrancheKeyType}_${TrancheKeyPeriod}${number}` | `${TrancheKeyType}_y`

export const trancheKeys: TrancheKey[] = [
  'base_y',
  'base_q1',
  'base_q2',
  'base_q3',
  'base_q4',
  'base_m1',
  'base_m2',
  'base_m3',
  'base_m4',
  'base_m5',
  'base_m6',
  'base_m7',
  'base_m8',
  'base_m9',
  'base_m10',
  'base_m11',
  'base_m12',
  'peak_y',
  'peak_q1',
  'peak_q2',
  'peak_q3',
  'peak_q4',
  'peak_m1',
  'peak_m2',
  'peak_m3',
  'peak_m4',
  'peak_m5',
  'peak_m6',
  'peak_m7',
  'peak_m8',
  'peak_m9',
  'peak_m10',
  'peak_m11',
  'peak_m12'
]

type MatchTranchePower = {
  [key in `${TrancheKey}_power`]?: number
}
type MatchTranchePrice = {
  [key in `${TrancheKey}_price`]?: number
}
type MatchTrancheLock = {
  [key in `${TrancheKey}_lock`]?: boolean
}
export type MatchTranche = MatchTranchePower & MatchTranchePrice & MatchTrancheLock

export type MaximizerOptions = {
  maximizer_max_surplus_percentage: number
}

export type AnnualHourlyProfile = {
  kind: 'ANNUAL_HOURLY_PROFILE'
  observations: number
  skip_weekends: boolean
}

export type CalendarMovingAverage = {
  kind: 'CALENDAR_MOVING_AVERAGE'
  window_days: number
  history_years: number
}

export type HistoricalYear = {
  kind: 'HISTORICAL_YEAR'
  year: number
}

export type RdnPricingMethod = AnnualHourlyProfile | CalendarMovingAverage | HistoricalYear

export type RdnMethodCapability = {
  method: RdnPricingMethod
  available: boolean
  unavailable_reason?: string
}

export type RdnPricingCapabilities = {
  candidate_pricing_cutoff_date?: string
  methods: RdnMethodCapability[]
  complete_historical_years: number[]
  historical_unavailable_reason?: string
}

export type RdnCalculationDetails = {
  method: RdnPricingMethod
  pricing_cutoff_date?: string
  rdn_percentage_adjustment: number
  displayed_price_formula: string
  excluded_costs: string
  days: Array<{
    calculation_day_of_year: number
    date: string
    hours: Array<{ hour_of_day: number; price: number; synthesized: boolean }>
  }>
}

export type MatchOptions = {
  rdn_energy_pricing_extra_percent: number
  rdn_pricing_method: RdnPricingMethod
  pricing_cutoff_date?: string
  tranche_energy_pricing_extra_percent: number
  cost_guarantee_of_source: number
  cost_excise: number
  cost_rdn_handling: number
  cost_ppa_handling: number
  cost_tranche_handling: number
  custom_current_energy_price?: number
  custom_offers_data: Array<
    MatchedOffersData & {
      lock: boolean
    }
  >
}

export type MatchingOffers = {
  status: MatchStatus
  current_reservation_length: number
  demand_energy_tariff_group: string
  diff_id: string
  match_id: number
  matched_offers_data: MatchedOffersData[]
  monthly_data: MonthlyData[]
  demand_data: {
    demand_id: number
    buyer_id: number
    electric_energy_meter_number: string
    energy_tariff_group: string
  }
  reservations_history: {
    continuation: boolean
    created_by: string
    created_date: string
    from_date: string
    last_modified_date: string
    reason: string
    status: 'REMOVED' | 'ACTIVE'
    to_date: string
  }[]
  status_history: {
    status: MatchStatus
    reason: string
    created_date: string
    modified_by: string
  }[]
  yearly_data: YearlyData
  interest_reported: boolean
  tranche?: MatchTranche
  options?: MatchOptions
}

export type PublicMatchingOffers = {
  diff_id: string
  match_id: number
  status: MatchStatus
  matched_offers_data: MatchedOffersData[]
  monthly_data: MonthlyData[]
  yearly_data: YearlyData
  interest_reported: boolean
  tranche?: MatchTranche
  options?: MatchOptions
}

export type ImbalanceHour = {
  hour: number
  shortage: number
  surplus: number
}

export type ImbalanceDetails = {
  days: { date: string; hours: ImbalanceHour[] }[]
}

export type SingleMatch = {
  id: number
  status: MatchStatus
  created_by: string
  created_date: string
  last_modified_date: string
  interest_reported: boolean
}

export type SearchMatchesData = {
  created_by?: string | null
  statuses: MatchStatus[]
  page_number?: number
  demand_id?: number | null
  offer_id?: number | null
}

export type SearchMatchesResponses = {
  total_pages: number
  total_elements: number
  number: number
  content: SingleMatch[]
}

export type SearchMatchesInputName = 'status' | 'created_by'

export type ClientMatchesData = {
  diff_id: string
  pricing_type: PricingType
  status: MatchStatus
  created_date: string
  interest_reported: boolean
}

export type ClientMatchesResponse = {
  content: ClientMatchesData[]
  total_pages: number
}

export type ClientMatchesBody = {
  page_number: number
  statuses: MatchStatus[]
}
